/* eslint-disable @tencent/i18next/no-literal-string */
import type { AgentTool, BaseAgent } from '@tencent/ai-sidebar-sdk';
import { STSlideSizeType } from '@tencent/slide-sdk-pb-model';
import { reaction } from 'mobx';
import { EDIT_TYPE } from '../../../edit-extension/edit-extension-export';
import { IPcAiService } from './pc-ai-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ISlideSizeService } from '@src/slide/application/service/slide-size-service/index.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { BackgroundFillTask } from './task/background-fill';
import { SolidColorResult, GradColorResult } from './task/background-fill/interface';

enum SlideSize {
    standard = 'standard',
    wide = 'wide',
    vertical = 'vertical',
    custom = 'custom',
}

interface ChangeBackgroundFillParams {
    range: 'current' | 'all';
    color: SolidColorResult | GradColorResult;
}

interface SearchImageParams {
    confirmSearch: boolean;
}

export class SlideAgent implements BaseAgent {
    public constructor(
        private backgroundFillTask: BackgroundFillTask,
        private aiService: IPcAiService,
        private uiDataService: IUiDataService,
        private masterLayoutEditService: IMasterLayoutEditService,
        private slideSizeService?: IDynamicService<ISlideSizeService>,
    ) {}

    public getTools(): Record<string, AgentTool> {
        return {
            changeSlideSize: {
                description: '更改PPT比例',
                exec: this.changeSlideSize,
            },
            enterMasterLayoutEdit: {
                description: '编辑PPT母版',
                exec: this.enterMasterLayoutEdit,
            },
            changeBackgroundFill: {
                description: '一键填充背景',
                exec: this.changeBackgroundFill as any,
            },
            searchImages: {
                description: '替换图片',
                exec: this.replaceImage as any,
            },
        };
    }

    private replaceImage = async ({ confirmSearch }: SearchImageParams) => {
        try {
            // 完成搜图替换后需弹出推荐指令框
            if (!confirmSearch) {
                this.aiService.showRecommendCard();
                return { success: true };
            }
            // 等待接口处理完毕后弹出推荐指令框
            setTimeout(() => this.aiService.showRecommendCard(), 1000);
            return { success: true, tips: '✅ 图片替换完毕' };
        } catch (error) {
            console.error(error);
            return { success: false, tips: '输入不合法' };
        }
    };

    private changeBackgroundFill = async ({ range, color }: ChangeBackgroundFillParams) => {
        try {
            this.backgroundFillTask.updateBackgroundFill(range, color);
            return { success: true, tips: '✅ 背景已更改' };
        } catch (error) {
            return { success: false, tips: '输入不合法' };
        }
    };

    private changeSlideSize = async ({ range }: { range: SlideSize }) => {
        const map = {
            [SlideSize.standard]: STSlideSizeType.screen4x3,
            [SlideSize.wide]: STSlideSizeType.screen16x9,
        };
        const reverseMap = {
            [STSlideSizeType.screen4x3]: SlideSize.standard,
            [STSlideSizeType.screen16x9]: SlideSize.wide,
        };

        if (range === SlideSize.standard || range === SlideSize.wide) {
            const slideSizeType = this.uiDataService.presentation?.props.type;
            if (slideSizeType && reverseMap[slideSizeType] === range) {
                return { success: true, tips: '当前已经是该比例' };
            }

            const sizeService = await this.slideSizeService?.promise();
            if (!sizeService) return { success: false };

            const sdkType = map[range];
            const res = await sizeService.showSlideSizeConfirmDialog(sdkType);
            if (!res) {
                return { success: true, tips: '已取消修改' };
            }
            return { success: true, tips: '✅ 比例已修改' };
        }

        if (range === SlideSize.custom) {
            const sizeService = await this.slideSizeService?.promise();
            if (!sizeService) return { success: false };

            sizeService.showSlideSizeDialog();
            return { success: true, tips: '✅ 已成功打开“自定义比例”功能' };
        }

        return { success: false, tips: '输入比例不合法' };
    };

    private enterMasterLayoutEdit = () => {
        const { masterLayoutEditService } = this;
        if (!masterLayoutEditService) return { success: false };

        masterLayoutEditService.changeEditType(EDIT_TYPE.MASTER_LAYOUT);
        // 退出母版编辑时重新打开助手
        const dispose = reaction(() => masterLayoutEditService.isMasterEditType, (isMasterEditType) => {
            if (!isMasterEditType) {
                dispose();
                this.aiService.showSideBar();
            }
        });
        return { success: true, tips: '✅ PPT成功进入母版视图' };
    };
}

